import { useEffect, useState } from "react";

import { AmplitudeFlagKey, AmplitudeVariantValue } from "@simplyk/common";

import { getChatWidgetRouteType } from "../utils";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitudeFeatureFlag } from "@/common/hooks/amplitude/useAmplitudeFeatureFlag";
import { RouteType } from "@/common/hooks/amplitude/useLogRouteChangePageVisits";

export type DonorSupportChannel = "forethought" | "assistant" | "pending";

export type DonorSupportChannelReason =
  | "not_donor_route"
  | "amplitude_unavailable"
  | "variant_control"
  | "kill_switch"
  | "variant_treatment";

export interface DonorSupportChannelResolution {
  channel: DonorSupportChannel;
  reason?: DonorSupportChannelReason;
  routeType: RouteType;
}

export const useDonorSupportChannel = (pathWithoutLocale: string): DonorSupportChannelResolution => {
  const { currentUser, userIsLoading } = useCurrentUserContext();
  const routeType = getChatWidgetRouteType(pathWithoutLocale);
  const isDonorPath = routeType === RouteType.DonorRoute;
  const isDonorRoute = isDonorPath && Boolean(currentUser?.id);

  const {
    isActive,
    loading: isFlagLoading,
    value: flagValue,
  } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.DonorAssistantWidget,
    userId: currentUser?.id,
    fallbackOnFail: false,
    skip: !isDonorRoute,
  });

  const {
    data: isDonorAssistantEnabled,
    isLoading: isKillSwitchLoading,
    isError: isKillSwitchError,
  } = trpc.getIsDonorAssistantEnabled.useQuery(undefined, { enabled: isDonorRoute && isActive });

  const [hasFlagRequestStarted, setHasFlagRequestStarted] = useState(false);

  useEffect(() => {
    setHasFlagRequestStarted(isDonorRoute);
  }, [isDonorRoute]);

  if (isDonorPath && userIsLoading) {
    return { channel: "pending", routeType };
  }

  if (!isDonorRoute) {
    return { channel: "forethought", reason: "not_donor_route", routeType };
  }

  if (isFlagLoading || !hasFlagRequestStarted) {
    return { channel: "pending", routeType };
  }

  if (!isActive) {
    const isFlagUnresolved = flagValue === null || flagValue === AmplitudeVariantValue.OFF;
    return {
      channel: "forethought",
      reason: isFlagUnresolved ? "amplitude_unavailable" : "variant_control",
      routeType,
    };
  }

  if (!isKillSwitchError && (isKillSwitchLoading || isDonorAssistantEnabled === undefined)) {
    return { channel: "pending", routeType };
  }

  if (!isDonorAssistantEnabled) {
    return { channel: "forethought", reason: "kill_switch", routeType };
  }

  return { channel: "assistant", reason: "variant_treatment", routeType };
};
